import React, { useState, useEffect } from 'react';
import {
  Paper,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  Box,
  createStyles,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
  SearchServiceAddress,
  Maybe,
} from '../../../../__generated__/pge-types';
import Modal from '../../../modal';
import Button from '../../../buttons';
import colors from '../../../../themes/main-colors';
import theme from '../../../../themes/theme';

const useStyles = makeStyles(() =>
  createStyles({
    cardStyle: { padding: '16px 0px' },
    container: {
      maxWidth: '680px',
      backgroundColor: colors.siteBackground,
      padding: '15px 20px',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '80vh',
        overflowY: 'auto',
      },
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      padding: '10px 25px',
    },
    title: {
      paddingLeft: '10px',
    },
    optionsContainer: {
      padding: '10px 0px',
    },
    options: {
      width: '100%',
      padding: '12px',
    },
    cancel: {
      marginRight: 10,
    },
    invalidAddressContainer: {
      padding: '10px 0px',
    },
    loader: {
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
    },
  }),
);

interface Props {
  isOpen: boolean;
  handleOnModalCancelled: () => void;
  userAddress: string;
  loading: boolean;
  addressSuggestions: Maybe<SearchServiceAddress>[] | undefined;
  handleSuggestedAddressSubmit: (selectedAddress: SearchServiceAddress) => void;
}

export const AddressSuggestion = ({
  isOpen,
  handleOnModalCancelled,
  userAddress,
  loading,
  addressSuggestions,
  handleSuggestedAddressSubmit,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [selectedAddress, setSelectedAddress] = useState<
    Maybe<SearchServiceAddress>
  >();
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const options = (addressSuggestions || []).slice(0, 5);

  useEffect(() => {
    setIsDisabled(true);
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      showCloseIcon={true}
      onClose={handleOnModalCancelled}
      data-testid="modalheader"
      title={
        <Typography variant="body1" className={classes.title}>
          {t('EDIT_ADDRESS_DETAILS')}
        </Typography>
      }
    >
      <Grid container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item container>
            <Typography>
              {t('WE_DID_NOT_FIND_EXACT_MATCH_ADDRESS', {
                userAddress,
              })}
            </Typography>
          </Grid>
        </Grid>
        {options?.length ? (
          <>
            <Grid container spacing={3} className={classes.optionsContainer}>
              <Grid item container>
                <Typography data-testid="select-text">
                  {t('SELECT_ONE_OF_THE_SIMILAR_ADDRESS')}
                </Typography>
              </Grid>
            </Grid>
            <Paper className={classes.options}>
              {loading ? (
                <Box className={classes.loader}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid item container direction={'column'} xs={12}>
                  <RadioGroup aria-label="change-auto-pay">
                    {options.map((address, i) => (
                      <FormControlLabel
                        key={i}
                        value={`${i}`}
                        control={<Radio color={'primary'} />}
                        label={`${address?.addressLine1}, ${address?.city}, ${address?.postal}`}
                        onChange={() => {
                          setIsDisabled(false);
                          setSelectedAddress({
                            ...address,
                            addressLine1: address?.addressLine1 || '',
                            city: address?.city || '',
                            postal: address?.postal || '',
                          });
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Grid>
              )}
            </Paper>
            <Box className={classes.footer}>
              <Button
                className={classes.cancel}
                color={'secondary'}
                variant={'outlined'}
                onClick={handleOnModalCancelled}
              >
                {t('CANCEL')}
              </Button>
              <Button
                disabled={isDisabled}
                color={'primary'}
                variant={'contained'}
                onClick={() =>
                  selectedAddress &&
                  handleSuggestedAddressSubmit(selectedAddress)
                }
              >
                {t('CONFIRM')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Grid
              container
              spacing={3}
              className={classes.invalidAddressContainer}
            >
              <Grid item container>
                <Typography data-testid="select-text">
                  {t('INVALID_ADDRESS')}
                </Typography>
              </Grid>
            </Grid>
            <Box
              className={classes.footer}
              style={{ float: 'right', width: '100%' }}
            >
              <Button
                color={'primary'}
                variant={'contained'}
                onClick={handleOnModalCancelled}
              >
                {t('CANCEL')}
              </Button>
            </Box>
          </>
        )}
      </Grid>
    </Modal>
  );
};
